import { Component } from "@theme/component";
import {
  CartGroupedSections,
  CartUpdateEvent,
  DiscountUpdateEvent,
  QuantitySelectorUpdateEvent,
  ThemeEvents,
} from "@theme/events";
import { getCart, isCartShaped } from "@theme/cart-state";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import { reconcileTierInline, whenTierSettled } from "@theme/shipping-protection";
import { debounce, fetchConfig, resetLoading } from "@theme/utilities";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #timeout = 5000;
  /** Counts the cart updates this has heard, so one overtaken while it waited for the cart can tell. */
  #cartUpdates = 0;
  /**
   * Serializes all cart mutations (qty, remove, gift toggle) so a second
   * click cannot race against an in-flight /cart/change.js — Shopify's
   * `line` index is 1-based and shifts as soon as a row is removed, and
   * gift-wrap reconciliation recomputes line keys mid-flight. While held,
   * the host element carries `cart-items-disabled` so pointer-events:none
   * also visually blocks the UI.
   * @type {Promise<unknown> | null}
   */
  #updateInFlight = null;

  /**
   * Runs `fn` after any in-flight mutation completes, keeping the disabled
   * state on for the entire chain.
   * @template T
   * @param {() => Promise<T>} fn
   * @returns {Promise<T>}
   */
  async #withLock(fn) {
    const prev = this.#updateInFlight;
    // Disable immediately so a second click queued behind the first sees
    // pointer-events:none with no microtask gap between operations.
    this.#disableCartItems();
    let resolve;
    const next = new Promise((r) => (resolve = r));
    this.#updateInFlight = next;
    try {
      if (prev) await prev.catch(() => {});
      return await fn();
    } finally {
      resolve();
      // Only re-enable when we're the tail of the queue — if another caller
      // already chained, let them clear the state when they finish.
      if (this.#updateInFlight === next) {
        this.#updateInFlight = null;
        this.#enableCartItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(CartGroupedSections.eventName, this.#onGroupedSections);

    // In the cart drawer this module loads on first need (cart-drawer.js), which can be a cart update that
    // came before it: take that one up, as the listener above would have, once this upgrade is done
    const missedUpdate = this.closest("cart-drawer-component")?.takeMissedCartUpdate?.();
    if (missedUpdate) queueMicrotask(() => this.#handleCartUpdate(missedUpdate));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(CartGroupedSections.eventName, this.#onGroupedSections);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    await this.#withLock(async () => {
      const lineItemRow = this.#getRowByLine(line);
      const parentKey = lineItemRow?.dataset.key;

      if (lineItemRow) {
        const removeButtons = lineItemRow.querySelectorAll(".cart-items__remove-button");
        removeButtons?.forEach((button) => {
          button?.classList.add("btn--loading");
        });
      }

      // 1. Update parent first; Shopify may clamp the quantity to available stock.
      await this.updateQuantity({
        ...(parentKey ? { id: parentKey } : { line }),
        line,
        quantity,
        action: "change",
      });

      // 2. Sync gift wrap lines using the actual quantity that Shopify accepted,
      // not the requested one (otherwise gift line could exceed stock-clamped parent).
      // Skip the entire roundtrip when the sync-quantity setting is off, otherwise
      // the spinner would spin while inner sync calls early-return for nothing.
      if (parentKey && this.#giftSyncQuantity) {
        // Parent line key may have changed after update (e.g. discount re-allocation
        // recomputes the line key hash), so re-read it from the morphed DOM and the
        // refreshed cart before syncing gift lines.
        const refreshedRow = this.#getRowByLine(line);
        const currentParentKey = refreshedRow?.dataset.key || parentKey;

        this.#setGiftLinesLoading(true, currentParentKey);
        try {
          const cart = await this.#fetchCartJson();
          const parentItem = cart.items.find((i) => i.key === currentParentKey) || cart.items[line - 1];
          const actualQuantity = parentItem?.quantity ?? 0;
          const effectiveParentKey = parentItem?.key || currentParentKey;
          const cartAfterPerLine = await this.#updateAssociatedGiftLines(effectiveParentKey, actualQuantity, cart);
          const cartForWholeOrder = cartAfterPerLine ?? cart;
          await this.#syncWholeOrderGiftLine(effectiveParentKey, actualQuantity, cartForWholeOrder);
        } finally {
          this.#setGiftLinesLoading(false, currentParentKey);
        }
      }
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line, event) {
    event?.preventDefault();

    await this.#withLock(async () => {
      const cartItemRowToRemove = this.#getRowByLine(line);
      const lineKey = cartItemRowToRemove?.dataset.key;

      if (cartItemRowToRemove) {
        const removeButtons = cartItemRowToRemove.querySelectorAll(".cart-items__remove-button");
        removeButtons.forEach((button) => {
          button?.classList.add("btn--loading");
        });
      }

      // Fast path: removing the last visible row would leave only auto-managed
      // lines (shipping protection, whole-order gift fee). /cart/change.js
      // would keep those lines, briefly render a non-empty cart with zero
      // visible rows, and then shipping-protection.js / the gift reconcile
      // would each fire their own roundtrips before we reach the truly-empty
      // state. POST /cart/clear.js zeroes everything atomically and gets us
      // there in a single morph.
      if (this.#wouldBeLastVisibleRow(cartItemRowToRemove)) {
        await this.#clearCart();
        return;
      }

      // Remove parent using the stable line key when available. Falling back to
      // `line` only is fragile: a concurrent gift/discount recompute can shift
      // indices and cause "line parameter is invalid". Do not sync whole-order
      // gift before remove — that recomputes line keys mid-flight. Nested
      // per-line gift children are auto-removed with the parent; reconcile
      // whole-order gift qty after remove.
      await this.updateQuantity({
        ...(lineKey ? { id: lineKey } : { line }),
        quantity: 0,
        action: "clear",
      });

      if (!this.#giftWrapPerProduct) {
        this.#setGiftLinesLoading(true);
        try {
          await this.#reconcileWholeOrderGiftAfterLineRemoval();
        } finally {
          this.#setGiftLinesLoading(false);
        }
      }
    });
  }

  /**
   * Returns true when removing `row` would leave zero user-facing rows.
   * Mirrors the visibility exclusion in cart-products.liquid: shipping
   * protection rows (`data-tier-protection="true"`) and gift-wrap child rows
   * (non-empty `data-parent-key`) are not counted as user-facing.
   * @param {HTMLElement | undefined} row
   * @returns {boolean}
   */
  #wouldBeLastVisibleRow(row) {
    if (!row) return false;
    const rows = this.refs.cartItemRows;
    if (!Array.isArray(rows) || rows.length === 0) return false;
    const visible = rows.filter(
      (r) => r.dataset.tierProtection !== "true" && r.dataset.parentKey === ""
    );
    return visible.length === 1 && visible[0] === row;
  }

  /**
   * Zeroes every line in the cart in a single POST and morphs the resulting
   * empty-cart sections. Used by the last-visible-row fast path so we collapse
   * what would otherwise be a 4-request chain (change → reconcile fetch →
   * remove protection → fetch) into a single roundtrip.
   */
  async #clearCart() {
    // An add-to-cart's tier add may still be in flight. Clearing over it can be
    // undone when the add lands, bringing back the line just removed (or leaving
    // only the protection line), so let it finish first.
    await whenTierSettled();

    const body = JSON.stringify(this.#getCartSectionsPayload());
    const res = await fetch(`${FoxTheme.routes.cart_url}/clear.js`, fetchConfig("json", { body }));
    const parsed = await res.json();

    resetLoading(this);

    if (!parsed?.sections?.[this.sectionId]) return;

    const resource = this.#cartResourceFromParsed(parsed);

    this.dispatchEvent(
      new CartUpdateEvent(resource, this.sectionId, {
        itemCount: 0,
        source: "cart-items-component",
        sections: parsed.sections,
        tierHandled: true,
      })
    );

    morphSection(this.sectionId, parsed.sections[this.sectionId]);

    this.#dispatchCartUpdated(parsed);
  }

  /**
   * Handles the per-line gift wrap checkbox toggle on a product row.
   * @param {Event} event
   */
  onPerLineGiftToggle = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const parentRow = input.closest("tr[data-line]");
    const lineIndex = Number(parentRow?.dataset.line);
    if (!lineIndex) return;

    const wrapper = input.closest(".cart-items__gift-wrap-line");
    wrapper?.classList.add("cart-items__gift-wrap-line--loading");

    try {
      await this.#withLock(async () => {
        if (input.checked) {
          await this.#addPerLineGift(lineIndex, input);
        } else {
          await this.#removePerLineGift(lineIndex);
        }
      });
    } finally {
      wrapper?.classList.remove("cart-items__gift-wrap-line--loading");
    }
  };

  /**
   * Adds a gift-wrap child line nested under the given parent.
   *
   * Strategy: remove the parent line, then re-add parent + child together in
   * a single /cart/add.js request using `parent_id = parent.variant_id`.
   * We can't reuse the existing parent via `parent_line_key` because
   * discounted lines have volatile keys that Shopify recomputes mid-request,
   * which would break the parent-child link.
   *
   * @param {number} lineIndex - 1-based index of the parent in cart.items.
   * @param {HTMLInputElement} lineInput
   */
  async #addPerLineGift(lineIndex, lineInput) {
    await this.#removeWholeOrderFeeLinesOnly();

    const cart = await this.#fetchCartJson();
    const parentItem = cart.items[lineIndex - 1];
    if (!parentItem) {
      console.warn("Parent line not found for gift wrap toggle");
      lineInput.checked = false;
      return;
    }

    const giftVariantId = this.#giftVariantId;
    const already = cart.items.some(
      (i) => Number(i.variant_id) === giftVariantId && i.parent_relationship?.parent_key === parentItem.key
    );
    if (already) {
      await this.#refreshCartSections();
      return;
    }

    const giftQuantity = this.#giftSyncQuantity ? parentItem.quantity || 1 : 1;

    const parentPayload = {
      id: parentItem.variant_id,
      quantity: parentItem.quantity,
      properties: { ...(parentItem.properties || {}) },
    };
    const sellingPlanId = parentItem.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) parentPayload.selling_plan = sellingPlanId;

    await fetch(
      FoxTheme.routes.cart_change_url,
      fetchConfig("json", {
        body: JSON.stringify({ id: parentItem.key, quantity: 0 }),
      })
    );

    const body = JSON.stringify({
      items: [
        parentPayload,
        {
          id: giftVariantId,
          quantity: giftQuantity,
          parent_id: parentItem.variant_id,
        },
      ],
      ...this.#getCartSectionsPayload(),
    });

    const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
    const parsed = await res.json();

    if (parsed.status && parsed.message) {
      /**
       * status: 422 "only N items were added" still mutates the cart; only treat as hard
       * failure when the response has no items/sections to render.
       **/
      if (parsed.status === 422) {
        if (this.#giftSyncQuantity) {
          const cartAfter = await this.#fetchCartJson();
          const giftInCart = cartAfter.items.find((i) => {
            if (Number(i.variant_id) !== giftVariantId || !i.parent_relationship?.parent_key) return false;
            const p = cartAfter.items.find((x) => x.key === i.parent_relationship.parent_key);
            return p && Number(p.variant_id) === Number(parentItem.variant_id);
          });
          const parentInCart =
            giftInCart && cartAfter.items.find((i) => i.key === giftInCart.parent_relationship.parent_key);

          if (parentInCart && giftInCart && parentInCart.quantity !== giftInCart.quantity) {
            await this.#refreshCartSections();
            this.#setGiftLinesLoading(true, parentInCart.key);
            try {
              await this.#updateAssociatedGiftLines(parentInCart.key, parentInCart.quantity, cartAfter);
            } finally {
              this.#setGiftLinesLoading(false, parentInCart.key);
            }
            return;
          }
        } else {
          // Fetch cart.js AND section HTML in parallel for instant morph
          const sectionIds = this.#gatherGroupedSectionIds();
          const sectionsData = {};
          const sectionPromises = sectionIds.map(async (sectionId) => {
            const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;
            const res = await fetch(sectionUrl);
            sectionsData[sectionId] = await res.text();
          });

          const cartPromise = fetch(FoxTheme.routes.cart).then((res) => res.json());

          Promise.all([cartPromise, ...sectionPromises])
            .then(([cart]) => {
              this.dispatchEvent(
                new CartUpdateEvent(cart, this.id, {
                  itemCount: cart.item_count || 0,
                  sections: Object.keys(sectionsData).length > 0 ? sectionsData : undefined,
                })
              );
            })
            .catch((error) => {
              console.error("Failed to fetch cart count:", error);
            });
        }
      }

      console.error(parsed.message);
      lineInput.checked = false;
      await fetch(
        FoxTheme.routes.cart_add_url,
        fetchConfig("json", { body: JSON.stringify({ items: [parentPayload] }) })
      );
      await this.#refreshCartSections();
      return;
    }

    await this.#applyPerLineCartResponse(parsed);
  }

  /**
   * Removes the nested gift-wrap child of a parent line.
   *
   * We resolve the child via `parent_relationship.parent_key` against the
   * parent at `lineIndex`, so a stale DOM attribute can't point us at a
   * wrong/volatile key after a discount recompute.
   *
   * @param {number} lineIndex - 1-based index of the parent in cart.items.
   */
  async #removePerLineGift(lineIndex) {
    if (!lineIndex) return;

    const cart = await this.#fetchCartJson();
    const parent = cart.items[lineIndex - 1];
    if (!parent) return;

    const giftVariantId = this.#giftVariantId;
    const child = cart.items.find(
      (i) => Number(i.variant_id) === giftVariantId && i.parent_relationship?.parent_key === parent.key
    );
    if (!child) return;

    const body = JSON.stringify({
      id: child.key,
      quantity: 0,
      ...this.#getCartSectionsPayload(),
    });
    const res = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", { body }));
    const parsed = await res.json();
    await this.#applyPerLineCartResponse(parsed);
  }

  /**
   * Removes standalone (whole-order) gift-wrap fee lines. Nested per-line
   * children are untouched. Called before adding a per-line gift so the cart
   * cannot end up in a mixed state when the setting was previously the other mode.
   */
  async #removeWholeOrderFeeLinesOnly() {
    const variantId = this.#giftVariantId;
    if (!variantId) return;

    while (true) {
      const cart = await this.#fetchCartJson();
      const item = cart.items.find(
        (i) => Number(i.variant_id) === variantId && i.parent_relationship?.parent_key == null
      );
      if (!item) break;

      const body = JSON.stringify({
        id: item.key,
        quantity: 0,
        ...this.#getCartSectionsPayload(),
      });
      const res = await fetch(FoxTheme.routes.cart_change_url, fetchConfig("json", { body }));
      const parsed = await res.json();
      await this.#applyPerLineCartResponse(parsed);
    }
  }

  /**
   * Reads the cart for the mutate-then-reread loops below. Always forces a fresh
   * request: these callers have just mutated the cart, so joining a request that
   * started before the mutation landed would return a stale snapshot.
   */
  async #fetchCartJson() {
    return getCart({ force: true });
  }

  /**
   * `/cart/change.js` and `/cart/update.js` return a full cart; passing it as the
   * CartUpdateEvent resource saves listeners (price-per-item, shipping-protection)
   * a `/cart.js` round-trip.
   *
   * `/cart/add.js` does not — it returns only the lines it just added, which look
   * cart-like enough to pass an `items` check. Hand those back as `{}` so
   * listeners re-read rather than mistaking the added lines for the whole cart.
   *
   * @param {object | undefined} parsed
   * @returns {object}
   */
  #cartResourceFromParsed(parsed) {
    return isCartShaped(parsed) ? parsed : {};
  }

  /**
   * After removing a cart line, reconcile whole-order gift wrap: when no non-gift
   * items remain, always strip fee lines (even if gift_wrap_sync_quantity is off).
   * Otherwise match fee qty to product totals only when sync-quantity is enabled.
   * Same-instance CartUpdateEvent skips #handleCartUpdate gift sync (event.target === this),
   * so removal flows must reconcile here.
   */
  async #reconcileWholeOrderGiftAfterLineRemoval() {
    if (this.#giftWrapPerProduct) return;

    const giftVariantId = this.#giftVariantId;
    if (!giftVariantId) return;

    const cart = await this.#fetchCartJson();

    let nonGiftQuantityTotal = 0;
    for (const item of cart.items) {
      if (Number(item.variant_id) === giftVariantId) continue;
      nonGiftQuantityTotal += item.quantity;
    }

    if (nonGiftQuantityTotal === 0) {
      await this.#removeWholeOrderFeeLinesOnly();
      return;
    }

    if (this.#giftSyncQuantity) {
      await this.#syncWholeOrderGiftLine("", 0, cart);
    }
  }

  /**
   * Parses each section a `/cart/*.js` response rendered, once: for the `item_count` it shows,
   * read here, and for its morph, which takes the parsed document.
   * @param {object | undefined} parsed - Parsed JSON from cart_change / cart_add.
   * @returns {{ documents: Record<string, Document>, itemCount: number }}
   */
  #parseSections(parsed) {
    /** @type {Record<string, Document>} */
    const documents = {};
    let itemCount = 0;

    for (const sid of Object.keys(parsed?.sections ?? {})) {
      const doc = new DOMParser().parseFromString(parsed.sections[sid], "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) {
        itemCount = parseInt(countEl.textContent, 10) || itemCount;
      }
      documents[sid] = doc;
    }

    return { documents, itemCount };
  }

  /**
   * Dispatches {@link CartUpdateEvent} so `cart-count` and sibling surfaces update (mirrors the
   * notification half of {@link #applyPerLineCartResponse}). Call after morphing `parsed.sections`.
   * @param {object | undefined} parsed - Parsed JSON from cart_change / cart_add.
   * @param {number} itemCount - The item count its sections show ({@link #parseSections}).
   */
  #dispatchCartUpdateFromParsedResponse(parsed, itemCount) {
    if (!parsed?.sections) return;

    const resource = this.#cartResourceFromParsed(parsed);

    document.querySelectorAll("cart-items-component").forEach((comp) => {
      if (!(comp instanceof HTMLElement)) return;
      const sid = comp.dataset.sectionId;
      if (sid && parsed.sections[sid]) {
        comp.dispatchEvent(
          new CartUpdateEvent(resource, sid, {
            itemCount,
            source: "cart-items-component",
            sections: parsed.sections,
          })
        );
      }
    });
  }

  /**
   * Morphs sections + dispatches cartUpdate event. Used after per-line gift
   * mutations so sibling cart surfaces (drawer, page) stay in sync and the
   * cart count badge updates.
   * @param {object | undefined} parsed
   */
  async #applyPerLineCartResponse(parsed) {
    if (!parsed?.sections) return;

    const { documents, itemCount } = this.#parseSections(parsed);
    for (const sid of Object.keys(documents)) {
      await morphSection(sid, documents[sid]);
    }

    this.#dispatchCartUpdateFromParsedResponse(parsed, itemCount);
  }

  /** @param {CartGroupedSections} event */
  #onGroupedSections = (event) => {
    event.detail.sections.push(this.sectionId);
  };

  /**
   * Dispatches {@link CartGroupedSections} and returns unique section ids for cart `sections` params.
   * @returns {string[]}
   */
  #gatherGroupedSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  /**
   * Re-fetches cart-related sections and morphs them in place. Used when we
   * short-circuit without hitting a cart-mutating endpoint (e.g. already-wrapped).
   */
  async #refreshCartSections() {
    const ids = this.#gatherGroupedSectionIds();
    await Promise.all(
      ids.map(async (sid) => {
        const url = `${window.location.pathname.split("?")[0]}?section_id=${sid}`;
        const res = await fetch(url);
        const html = await res.text();
        await morphSection(sid, html);
      })
    );
  }

  /**
   * Look up a cart item row by its 1-based line index. DOM order may not match
   * `cart.items` order (e.g. gift wrap rows are rendered last), so we resolve by
   * the `data-line` attribute instead of array index.
   *
   * @param {number} line - 1-based line index from cart.items.
   * @returns {HTMLTableRowElement | undefined}
   */
  #getRowByLine(line) {
    const rows = this.refs.cartItemRows;
    if (!Array.isArray(rows)) return undefined;
    return /** @type {HTMLTableRowElement | undefined} */ (rows.find((row) => Number(row.dataset.line) === line));
  }

  /**
   * Toggles loading on gift wrap rows via `is-quantity-syncing` on the `<tr>`
   * (gift lines use the quantity sync spinner only; no remove control). Mirrors
   * the parent line's loading state while gift sync is in flight, since the morph
   * from the gift sync request may not run when nothing needs to change.
   *
   * Only toggles:
   * - The per-line nested gift row whose `data-parent-key` matches `parentKey`
   *   (the gift line nested under the parent being changed).
   * - Any whole-order gift row (no `data-parent-key`), because its quantity may
   *   sync on every parent line change when sync-quantity is enabled.
   *
   * @param {boolean} on
   * @param {string} [parentKey]
   */
  #setGiftLinesLoading(on, parentKey = "") {
    const rows = this.refs.cartItemRows;
    if (!Array.isArray(rows)) return;
    rows.forEach((row) => {
      if (!row.classList.contains("cart-items__table-row--gift-wrap")) return;

      const rowParentKey = row.dataset.parentKey || "";
      const isWholeOrderGift = !rowParentKey;
      const isLinkedNestedGift = parentKey && rowParentKey === parentKey;

      if (!isWholeOrderGift && !isLinkedNestedGift) return;

      row.classList.toggle("is-quantity-syncing", on);
    });
  }

  /**
   * Builds the Shopify `sections` payload via {@link CartGroupedSections} listeners.
   */
  #getCartSectionsPayload() {
    return {
      sections: this.#gatherGroupedSectionIds().join(","),
      sections_url: window.location.pathname,
    };
  }

  /**
   * Morphs each rendered section returned by a `/cart/*.js` response.
   * @param {object | undefined} parsed - Parsed JSON from cart_change_url / cart_add_url.
   * @returns {number} The item count the sections show, for {@link #dispatchCartUpdateFromParsedResponse}.
   */
  #morphSectionsFromResponse(parsed) {
    const { documents, itemCount } = this.#parseSections(parsed);
    Object.keys(documents).forEach((sid) => morphSection(sid, documents[sid]));
    return itemCount;
  }

  get #giftVariantId() {
    return Number(this.dataset.giftVariantId) || 0;
  }

  get #giftWrapPerProduct() {
    return this.dataset.giftWrapPerProduct === "true";
  }

  get #giftSyncQuantity() {
    return this.dataset.giftSyncQuantity === "true";
  }

  /**
   * Updates quantity of the per-line gift wrap child nested under the given parent.
   * Uses Shopify's native `parent_relationship.parent_key` to resolve linkage.
   *
   * @param {string} parentKey - The parent line's stable cart line key.
   * @param {number} quantity - The new quantity (0 to remove).
   * @param {{ items?: unknown[] } | undefined} [preloadedCart]
   * @returns {Promise<{ items?: unknown[] } | undefined>} Full cart from last `/cart/change` when lines were updated; otherwise `undefined` (caller may reuse `preloadedCart`).
   */
  async #updateAssociatedGiftLines(parentKey, quantity, preloadedCart) {
    if (!this.#giftWrapPerProduct) return undefined;
    if (quantity > 0 && !this.#giftSyncQuantity) return undefined;
    if (!parentKey) return undefined;

    const giftVariantId = this.#giftVariantId;
    if (!giftVariantId) return undefined;

    const cart = preloadedCart && Array.isArray(preloadedCart.items) ? preloadedCart : await this.#fetchCartJson();
    const giftItems = cart.items.filter(
      (i) => Number(i.variant_id) === giftVariantId && i.parent_relationship?.parent_key === parentKey
    );

    const sectionsPayload = this.#getCartSectionsPayload();
    let lastParsed;
    for (const giftItem of giftItems) {
      if (giftItem.quantity === quantity) continue;
      const res = await fetch(
        FoxTheme.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify({ id: giftItem.key, quantity, ...sectionsPayload }),
        })
      );
      lastParsed = await res.json();
    }

    const itemCount = this.#morphSectionsFromResponse(lastParsed);
    this.#dispatchCartUpdateFromParsedResponse(lastParsed, itemCount);

    return lastParsed && Array.isArray(lastParsed.items) ? lastParsed : undefined;
  }

  /**
   * Syncs the whole-order gift wrap line quantity to the total non-gift quantity.
   * @param {string} changingItemKey - The key of the item being changed.
   * @param {number} newQuantity - The new quantity for that item.
   * @param {{ items?: { variant_id?: number; quantity?: number; key?: string; parent_relationship?: { parent_key?: string | null } }[] } | undefined} [preloadedCart] - When set (e.g. from a just-fetched /cart.js), skip a duplicate fetch.
   */
  async #syncWholeOrderGiftLine(changingItemKey, newQuantity, preloadedCart) {
    if (this.#giftWrapPerProduct) return;
    if (newQuantity > 0 && !this.#giftSyncQuantity) return;

    const giftVariantId = this.#giftVariantId;
    if (!giftVariantId) return;

    const cart = preloadedCart && Array.isArray(preloadedCart.items) ? preloadedCart : await this.#fetchCartJson();

    const giftItem = cart.items.find(
      (i) => Number(i.variant_id) === giftVariantId && i.parent_relationship?.parent_key == null
    );
    if (!giftItem) return;

    let totalQuantity = 0;
    for (const item of cart.items) {
      if (Number(item.variant_id) === giftVariantId) continue;
      totalQuantity += item.key === changingItemKey ? newQuantity : item.quantity;
    }

    if (giftItem.quantity === totalQuantity) return;

    const res = await fetch(
      FoxTheme.routes.cart_change_url,
      fetchConfig("json", {
        body: JSON.stringify({
          id: giftItem.key,
          quantity: totalQuantity,
          ...this.#getCartSectionsPayload(),
        }),
      })
    );
    const parsed = await res.json();
    const itemCount = this.#morphSectionsFromResponse(parsed);
    this.#dispatchCartUpdateFromParsedResponse(parsed, itemCount);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The 1-based line index.
   * @param {string} [config.id] - The line item key (used instead of line when provided).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { line, quantity, id } = config;

    const sectionsToUpdate = this.#gatherGroupedSectionIds();

    // Send either `id` (stable line key) or `line` (1-based index), never both.
    // Shopify's API treats them as alternatives, and a stale `line` index
    // alongside a valid `id` has produced "no valid id or line parameter"
    // when concurrent mutations recompute the cart.
    const body = JSON.stringify({
      ...(id ? { id } : { line }),
      quantity: quantity,
      sections: sectionsToUpdate.join(","),
      sections_url: window.location.pathname,
    });

    // Don't overlap a tier change still in flight (see `#clearCart`). The line was
    // resolved to its key before this, so the tier line being added at the top
    // of the cart doesn't change which line this updates.
    return whenTierSettled()
      .then(() => fetch(`${FoxTheme.routes.cart_change_url}`, fetchConfig("json", { body })))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        let parsedResponseText = JSON.parse(responseText);

        // Roll any shipping-protection tier change for the new subtotal into the
        // same response so we morph once with the final state. Returns the input
        // unchanged when the cart is already in the correct tier or when the
        // response shape is unusable (e.g. errors). Pair with `tierHandled: true`
        // on the dispatched event so shipping-protection's reactive listener
        // doesn't re-run the same work.
        if (!parsedResponseText.errors) {
          parsedResponseText = await reconcileTierInline(parsedResponseText, sectionsToUpdate);
        }

        resetLoading(this);

        // Even with errors, backend may have updated cart to max available
        // Update UI and cart count if we have sections
        if (parsedResponseText.sections && parsedResponseText.sections[this.sectionId]) {
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            "text/html"
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          const resource = this.#cartResourceFromParsed(parsedResponseText);

          this.dispatchEvent(
            new CartUpdateEvent(resource, this.sectionId, {
              itemCount: newCartItemCount,
              source: "cart-items-component",
              sections: parsedResponseText.sections,
              tierHandled: true,
            })
          );

          // Parsed once, above, for the count as well
          morphSection(this.sectionId, newSectionHTML);

          this.#dispatchCartUpdated(parsedResponseText);
        } else if (parsedResponseText.errors) {
          // No sections in error response - fetch cart.js for accurate count and quantity

          const cartSectionsData = {};
          let cartJson = null;
          const cartSectionsPromises = sectionsToUpdate.map(async (sectionId) => {
            const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;

            const res = await fetch(sectionUrl);
            const html = await res.text();

            cartSectionsData[sectionId] = html;
          });

          const cartJsonPromises = fetch(FoxTheme.routes.cart)
            .then((res) => res.json())
            .then((data) => {
              cartJson = data;
            });

          await Promise.all([...cartSectionsPromises, cartJsonPromises]);

          cartJson["sections"] = cartSectionsData;

          this.dispatchEvent(
            new CartUpdateEvent(cartJson, "", {
              itemCount: cartJson.item_count || 0,
              sections: cartJson.sections,
            })
          );

          morphSection(this.sectionId, cartJson.sections[this.sectionId]);

          this.#dispatchCartUpdated(cartJson);
        }

        /**
         * Show error message if exists (e.g. quantity exceeds available)
         * Call after morph section to avoid message disappear
         */
        if (parsedResponseText.errors && line != null) {
          this.#handleCartError(line, parsedResponseText);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    if (event?.detail?.sourceId === this.sectionId) return;
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");

    setTimeout(() => {
      cartItemErrorContainer.classList.add("hidden");
    }, this.#timeout);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | import("@theme/events").CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    // Any update makes one still waiting for the cart below out of date, this component's own included
    const update = ++this.#cartUpdates;

    // Self-dispatched: updateQuantity() already morphed sections and called #dispatchCartUpdated.
    if (event.target === this) return;

    // Reuse cart data already present in the event (from a cart/change or
    // cart/update response) to avoid a /cart.js round-trip. Add responses are not
    // cart-shaped, so this misses on add-to-cart — `getCart()` (not the forcing
    // `#fetchCartJson`) shares that request with the other cartUpdate listeners,
    // which are dispatched in this same tick and need the same snapshot.
    const preloadedResource = event.detail?.resource;
    const cartJson = isCartShaped(preloadedResource) ? preloadedResource : await getCart();

    // A later update came in while this one waited: its sections are newer, so leave the morph to it rather
    // than put this one's back over it (the update the drawer kept for this module while it loaded can be
    // overtaken by shipping protection's, which follows it).
    if (update !== this.#cartUpdates) return;

    // Copy before attaching sections: getCart() hands the same object to every
    // listener sharing the request, and the third-party `cart:updated` payload
    // must not leak a `sections` key into the others' view of the cart.
    this.#dispatchCartUpdated({ ...cartJson, sections: event.detail?.data?.sections });

    if (event instanceof DiscountUpdateEvent) {
      if (event?.detail?.sourceId === this.sectionId) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // External cart updates (add to cart, AJAX errors with partial cart, etc.) do not
    // run #onQuantityChange. Reconcile whole-order gift qty when sync mode is on.
    // #syncWholeOrderGiftLine no-ops when there is no fee line or qty already matches.
    if (!this.#giftWrapPerProduct && this.#giftSyncQuantity) {
      this.#setGiftLinesLoading(true);
      try {
        await this.#syncWholeOrderGiftLine("", 0, cartJson);
      } finally {
        this.#setGiftLinesLoading(false);
      }
    }
  };

  /**
   * Dispatches a cart updated event for 3rd party.
   * @param {Object} cart - The cart data.
   */
  #dispatchCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdated, {
        detail: { cart },
      })
    );
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
